import React, { useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useAssessment } from './hooks/useAssessment';
import { assessmentService } from './services/assessmentService';
import { FEEDBACK_THRESHOLD } from './config/constants';

// Layout Components
import Header from './components/Layout/Header';
import LoadingSpinner from './components/common/LoadingSpinner';
import Alert from './components/common/Alert';

// Auth Components
import LoginPage from './components/Auth/LoginPage';
import ProtectedRoute from './components/Auth/ProtectedRoute';

// Home & Case Components
import HomeScreen from './components/Home/HomeScreen';
import CaseList from './components/Case/CaseList';
import CaseInitialsForm from './components/Case/CaseInitialsForm';

// Assessment Components
import AssessmentForm from './components/Assessment/AssessmentForm';
import ProcessingView from './components/Assessment/ProcessingView';
import AssessmentResults from './components/Assessment/AssessmentResults';

// Modal Components
import ConfirmationModal from './components/Modals/ConfirmationModal';
import AnonymizeModal from './components/Modals/AnonymizeModal';
import LimitModal from './components/Modals/LimitModal';
import FeedbackModal from './components/Modals/FeedbackModal';
import SaveCaseModal from './components/Modals/SaveCaseModal';

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Authentication
  const {
    authToken,
    userInfo,
    isLoading,
    assessmentsRemaining,
    monthlyLimit,
    login,
    logout,
    fetchUserUsage,
    updateAssessmentsRemaining
  } = useAuth();

  // Assessment
  const {
    notes,
    setNotes,
    results,
    editingItem,
    editedScore,
    setEditedScore,
    currentCase,
    cases,
    isLoadingCases,
    isSynced,
    analyzeNotes,
    startEditing,
    saveEdit,
    cancelEdit,
    revertToAI,
    startNewAssessment,
    submitCaseInitials,
    selectCaseForAssessment,
    clearCurrentCase,
    saveAssessmentForCase
  } = useAssessment(
    authToken, 
    logout, 
    (usage) => {
      updateAssessmentsRemaining(usage.remaining);
    },
    navigate
  );

  // Modal states
  const [showNewAssessmentModal, setShowNewAssessmentModal] = useState(false);
  const [showAnonymizeModal, setShowAnonymizeModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showSaveCaseModal, setShowSaveCaseModal] = useState(false);

  // Handle analyze click
  const handleAnalyzeClick = () => {
    if (!authToken) {
      alert('Please login first');
      login();
      return;
    }
    setShowAnonymizeModal(true);
  };

  // Confirm analyze
  const confirmAnalyze = () => {
    setShowAnonymizeModal(false);

    // Check if user has assessments remaining
    if (assessmentsRemaining !== null && assessmentsRemaining <= 0) {
      setShowLimitModal(true);
      return;
    }

    analyzeNotes();
  };

  // Handle feedback submit
  const handleFeedbackSubmit = async (feedbackData) => {
    try {
      const data = await assessmentService.submitFeedback(feedbackData, authToken);
      
      // Refresh usage
      if (data.bonusCredits !== undefined) {
        await fetchUserUsage();
      }
      
      alert('🎉 Thank you for your feedback! 5 bonus assessments have been added to your account.');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  };

  // Handle new assessment
  const handleNewAssessment = () => {
    setShowNewAssessmentModal(true);
  };

  const confirmNewAssessment = () => {
    startNewAssessment();
    setShowNewAssessmentModal(false);
  };

  // Handle save to case
  const handleSaveToCase = () => {
    setShowSaveCaseModal(true);
  };

  const confirmSaveToCase = (caseName, caseId) => {
    saveAssessmentForCase(caseName, caseId);
    setShowSaveCaseModal(false);
    alert(`✅ Assessment saved to case: ${caseName}`);
  };

  // Show loading spinner
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Show login page if not authenticated
  if (!authToken) {
    return <LoginPage onLogin={login} />;
  }

  // Determine if we should show low credits warning
  const showLowCreditsWarning = 
    assessmentsRemaining !== null && 
    assessmentsRemaining > 0 && 
    assessmentsRemaining < (monthlyLimit * FEEDBACK_THRESHOLD) && 
    location.pathname === '/assessment';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Show on all views */}
      <Header
        authToken={authToken}
        userInfo={userInfo}
        assessmentsRemaining={assessmentsRemaining}
        monthlyLimit={monthlyLimit}
        onLogin={login}
        onLogout={logout}
        view={location.pathname}
        onBack={() => navigate('/')}
      />

      <div className="pt-16">
        {/* Low Credits Warning Banner */}
        {showLowCreditsWarning && (
          <Alert
            type="warning"
            message={`Your credits are running low! You have ${assessmentsRemaining} of ${monthlyLimit} remaining. Complete our quick feedback form to unlock 5 bonus credits instantly!`}
            action={() => setShowFeedbackModal(true)}
            actionLabel="Get 5 Bonus Credits"
          />
        )}

        {/* Main Content - Protected Routes */}
        <Routes>
          <Route path="/" element={
            <ProtectedRoute authToken={authToken} isLoading={isLoading}>
              <HomeScreen
                onNewAssessment={() => navigate('/assessment')}
                onViewCases={() => navigate('/cases')}
              />
            </ProtectedRoute>
          } />

          <Route path="/cases" element={
            <ProtectedRoute authToken={authToken} isLoading={isLoading}>
              <CaseList
                cases={cases}
                token={authToken}
                onSelectCase={(caseItem) => {
                  selectCaseForAssessment(caseItem.caseId);
                  navigate('/assessment');
                }}
                onAddNewCase={() => navigate('/cases/new')}
              />
            </ProtectedRoute>
          } />

          <Route path="/cases/new" element={
            <ProtectedRoute authToken={authToken} isLoading={isLoading}>
              <CaseInitialsForm
                onSubmit={submitCaseInitials}
                onCancel={() => navigate('/cases')}
              />
            </ProtectedRoute>
          } />

          <Route path="/assessment" element={
            <ProtectedRoute authToken={authToken} isLoading={isLoading}>
              <AssessmentForm
                authToken={authToken}
                notes={notes}
                setNotes={setNotes}
                onAnalyze={handleAnalyzeClick}
                onLogin={login}
                currentCase={currentCase}
                cases={cases}
                onSelectCase={selectCaseForAssessment}
                onClearCase={clearCurrentCase}
              />
            </ProtectedRoute>
          } />

          <Route path="/assessment/processing" element={
            <ProtectedRoute authToken={authToken} isLoading={isLoading}>
              <ProcessingView notes={notes} />
            </ProtectedRoute>
          } />

          <Route path="/assessment/results" element={
            <ProtectedRoute authToken={authToken} isLoading={isLoading}>
              {results ? (
                <AssessmentResults
                  results={results}
                  notes={notes}
                  editingItem={editingItem}
                  editedScore={editedScore}
                  onStartEdit={startEditing}
                  onSaveEdit={saveEdit}
                  onCancelEdit={cancelEdit}
                  onRevert={revertToAI}
                  onNewAssessment={handleNewAssessment}
                  onSaveToCase={handleSaveToCase}
                  onScoreChange={setEditedScore}
                  currentCase={currentCase}
                />
              ) : (
                <div>No results available</div>
              )}
            </ProtectedRoute>
          } />
        </Routes>
      </div>

      {/* Modals */}
      <ConfirmationModal
        isOpen={showNewAssessmentModal}
        title="Start a new assessment?"
        message="Your current results will be lost. Continue?"
        onConfirm={confirmNewAssessment}
        onCancel={() => setShowNewAssessmentModal(false)}
        confirmText="Start New Assessment"
      />

      <AnonymizeModal
        isOpen={showAnonymizeModal}
        onConfirm={confirmAnalyze}
        onCancel={() => setShowAnonymizeModal(false)}
      />

      <LimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        onProvideFeedback={() => setShowFeedbackModal(true)}
      />

      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onSubmit={handleFeedbackSubmit}
        assessmentsRemaining={assessmentsRemaining}
        userEmail={userInfo?.email}
      />

      <SaveCaseModal
        isOpen={showSaveCaseModal}
        onClose={() => setShowSaveCaseModal(false)}
        onSave={confirmSaveToCase}
        existingCase={currentCase}
      />
    </div>
  );
};

export default App;
